'use client'

import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import { Textarea } from '@/components/ui/textarea'
import { FieldValues, Path, UseFormReturn } from 'react-hook-form'
import { cn } from '@/lib/utils/utils'
import { formFieldTextareaClassName } from '@/components/form/formFieldSurface'

/**
 * Textarea form field component
 * @template TFormValues - The form values type extending FieldValues
 */
interface TextareaFormFieldProps<TFormValues extends FieldValues> {
    form: UseFormReturn<TFormValues>
    name: Path<TFormValues>
    disabled?: boolean
    label?: string
    /** Label above textarea, textarea always full width (no side-by-side layout on large screens) */
    stackLabel?: boolean
    placeholder?: string
    value?: string
    onChange?: (value: string) => void
    /** Merged after default myworldesk surface (from `formFieldSurface`) */
    textareaClassName?: string
}

export const TextareaFormField = <TFormValues extends FieldValues>({
    form,
    name,
    disabled,
    label,
    stackLabel,
    placeholder,
    value,
    onChange,
    textareaClassName
}: TextareaFormFieldProps<TFormValues>) => {
    const stacked = stackLabel ?? false
    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem>
                    <div
                        className={cn(
                            'flex flex-col',
                            label && !stacked && 'lg:flex-row',
                            stacked && label && 'gap-2'
                        )}>
                        {label && (
                            <FormLabel
                                className={cn(stacked ? 'text-left' : 'lg:basis-1/2 content-center mb-2 lg:mb-0')}>
                                {label}
                            </FormLabel>
                        )}
                        <FormControl className={cn(label && !stacked && 'lg:basis-1/2', 'w-full min-w-0')}>
                            <Textarea
                                disabled={disabled ? disabled : false}
                                placeholder={placeholder || ''}
                                {...field}
                                value={value ?? field.value ?? ''}
                                onChange={(e) => {
                                    field.onChange(e)
                                    onChange?.(e.target.value)
                                }}
                                className={cn(formFieldTextareaClassName, textareaClassName)}
                            />
                        </FormControl>
                    </div>
                    <FormMessage className={label && !stacked ? 'text-right' : ''} />
                </FormItem>
            )}
        />
    )
}
